import { getFeatureFlags, setFeatureOverride, type FeatureFlagKey } from './config/featureFlags';
import { siteContent } from './content/siteContent';
import './styles.css';

const appRoot = document.querySelector<HTMLDivElement>('#app');

if (!appRoot) {
  throw new Error('App root was not found.');
}

const app = appRoot;

type Theme = 'light' | 'dark';

function getInitialTheme(): Theme {
  const savedTheme = window.localStorage.getItem('tae.theme');
  if (savedTheme === 'light' || savedTheme === 'dark') {
    return savedTheme;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function setTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  window.localStorage.setItem('tae.theme', theme);
}

function shouldShowFeatureAdmin(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get('featureAdmin') === 'true';
}

function renderFeatureToggle(key: FeatureFlagKey, label: string, enabled: boolean): string {
  return `
    <label class="flag-toggle">
      <input data-feature-flag="${key}" type="checkbox" ${enabled ? 'checked' : ''} />
      <span>${label}</span>
    </label>
  `;
}

function render(): void {
  const flags = getFeatureFlags();

  app.innerHTML = `
    <header class="site-header">
      <a class="brand" href="#top" aria-label="${siteContent.businessName} home">
        <span class="logo-slot" aria-hidden="true">
          <img src="/symbol.jpg" alt="" />
        </span>
        <span>${siteContent.businessName}</span>
      </a>
      <nav class="top-nav" aria-label="Primary navigation">
        <a href="#services">Services</a>
        <a href="#accomplishments">Accomplishments</a>
        <a href="#contact">Contact</a>
      </nav>
      <button class="theme-toggle" type="button" aria-label="Toggle dark theme" title="Toggle dark theme">
        <span class="theme-icon" aria-hidden="true"></span>
      </button>
    </header>

    <main id="top">
      <section class="hero-section">
        <div class="hero-copy">
          <p class="eyebrow">Software Development • Cloud Architecture • Consulting</p>
          <h1>${siteContent.headline}</h1>
          <p class="hero-summary">${siteContent.summary}</p>
          <div class="hero-actions">
            <a class="button primary" href="#contact">Start a conversation</a>
            <a class="button secondary" href="#accomplishments">View experience</a>
          </div>
        </div>
        <aside class="profile-panel" aria-label="Profile photo placeholder">
          <div class="photo-slot">
            <img src="/profile-photo.jpg" alt="${siteContent.ownerName}" />
          </div>
          <div>
            <p class="profile-name">${siteContent.ownerName}</p>
            <p class="profile-role">Software Developer and Engineering Leader</p>
          </div>
        </aside>
      </section>

      ${
        flags.availabilityBanner
          ? `<section class="availability-band" aria-label="Availability">
              <strong>Consulting availability opening soon.</strong>
              <span>Custom web applications, cloud architecture, and development leadership engagements.</span>
            </section>`
          : ''
      }

      <section class="content-section" id="services">
        <div class="section-heading">
          <p class="eyebrow">Services</p>
          <h2>Focused help where software decisions matter.</h2>
        </div>
        <div class="service-grid">
          ${siteContent.services
            .map(
              (service) => `
                <article class="service-card">
                  <h3>${service.title}</h3>
                  <p>${service.description}</p>
                </article>
              `,
            )
            .join('')}
        </div>
      </section>

      <section class="content-section split-section" id="accomplishments">
        <div class="section-heading">
          <p class="eyebrow">Experience</p>
          <h2>Enterprise delivery with startup pace.</h2>
        </div>
        <div class="accomplishment-list" aria-label="Key accomplishments">
          ${siteContent.accomplishments
            .map((item) => `<div class="accomplishment-item">${item}</div>`)
            .join('')}
        </div>
      </section>

      <section class="content-section">
        <div class="section-heading">
          <p class="eyebrow">Technical Range</p>
          <h2>Full-stack implementation through cloud operations.</h2>
        </div>
        <div class="stack-grid">
          ${siteContent.stackGroups
            .map(
              (group) => `
                <article class="stack-group">
                  <h3>${group.title}</h3>
                  <div class="tag-list">
                    ${group.items.map((item) => `<span>${item}</span>`).join('')}
                  </div>
                </article>
              `,
            )
            .join('')}
        </div>
      </section>

      ${
        flags.caseStudies
          ? `<section class="content-section">
              <div class="section-heading">
                <p class="eyebrow">Case Studies</p>
                <h2>Selected delivery stories will be published here.</h2>
              </div>
            </section>`
          : ''
      }

      <section class="contact-section" id="contact">
        <div>
          <p class="eyebrow">Contact</p>
          <h2>Bring a clear technical partner into the work.</h2>
          <p>
            For project inquiries, consulting availability, or architecture review requests, reach out directly.
          </p>
        </div>
        ${
          flags.contactForm
            ? `<form class="contact-form">
                <label>
                  Name
                  <input name="name" autocomplete="name" />
                </label>
                <label>
                  Email
                  <input name="email" type="email" autocomplete="email" />
                </label>
                <label>
                  Message
                  <textarea name="message" rows="4"></textarea>
                </label>
                <button class="button primary" type="submit">Send</button>
              </form>`
            : `<a class="button primary" href="mailto:info.tandersonenterprises@proton.me">Email T. Anderson Enterprises</a>`
        }
      </section>

      ${
        shouldShowFeatureAdmin()
          ? `<section class="feature-admin" aria-label="Feature flags">
              <h2>Feature Flags</h2>
              <div class="flag-grid">
                ${renderFeatureToggle('availabilityBanner', 'Availability banner', flags.availabilityBanner)}
                ${renderFeatureToggle('caseStudies', 'Case studies area', flags.caseStudies)}
                ${renderFeatureToggle('contactForm', 'Contact form shell', flags.contactForm)}
              </div>
            </section>`
          : ''
      }
    </main>

    <footer class="site-footer">
      <span>${siteContent.businessName}</span>
      <span>Custom software development and consulting</span>
    </footer>
  `;

  bindEvents();
}

function bindEvents(): void {
  const themeButton = document.querySelector<HTMLButtonElement>('.theme-toggle');
  themeButton?.addEventListener('click', () => {
    const nextTheme = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
  });

  document.querySelectorAll<HTMLInputElement>('[data-feature-flag]').forEach((input) => {
    input.addEventListener('change', () => {
      setFeatureOverride(input.dataset.featureFlag as FeatureFlagKey, input.checked);
      render();
    });
  });
}

setTheme(getInitialTheme());
render();
